import React, { useState, useMemo } from "react";
import { Header } from "@/components/Header";
import { storage } from "@/lib/storage";
import { formatMoney, CURRENCIES } from "@/lib/currencies";
import { ArrowDownLeft, ArrowUpRight, Filter, History as HistoryIcon, Trash2 } from "lucide-react";

const METHOD_LABEL = {
  qr: "QR Code",
  manual: "Manual Entry",
  nfc: "NFC Tap",
  cash: "Cash",
  upi: "UPI",
  wallet: "Wallet",
};

export default function History() {
  const [txs, setTxs] = useState(storage.getTransactions());
  const [filter, setFilter] = useState("all"); // all | received | sent
  const [currencyFilter, setCurrencyFilter] = useState("all");

  const filtered = useMemo(() => {
    return txs.filter((t) => {
      if (filter !== "all" && t.direction !== filter) return false;
      if (currencyFilter !== "all" && t.currency !== currencyFilter) return false;
      return true;
    });
  }, [txs, filter, currencyFilter]);

  const totals = useMemo(() => {
    const received = {};
    const sent = {};
    txs.forEach((t) => {
      const bag = t.direction === "received" ? received : sent;
      bag[t.currency] = (bag[t.currency] || 0) + Number(t.amount);
    });
    return { received, sent };
  }, [txs]);

  const clearAll = () => {
    if (window.confirm("Clear all transactions on this device?")) {
      localStorage.removeItem("opg_transactions");
      setTxs([]);
    }
  };

  return (
    <div className="page-shell" data-testid="history-page">
      <Header title="Transaction History" subtitle="Stored locally on this device" />

      <div className="grid grid-cols-2 gap-3 mb-5 fade-slide-up">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <ArrowDownLeft size={14} className="text-[#E6B455]" />
            <div className="stat-label">Received</div>
          </div>
          <div className="space-y-1 mt-2">
            {Object.keys(totals.received).length ? (
              Object.entries(totals.received).map(([c, v]) => (
                <div key={c} className="font-semibold text-[#F7E6B4]" data-testid={`total-received-${c}`}>
                  {formatMoney(v, c)}
                </div>
              ))
            ) : (
              <div className="text-sm text-[#F7E6B4]/55">—</div>
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <ArrowUpRight size={14} className="text-[#E6B455]" />
            <div className="stat-label">Sent</div>
          </div>
          <div className="space-y-1 mt-2">
            {Object.keys(totals.sent).length ? (
              Object.entries(totals.sent).map(([c, v]) => (
                <div key={c} className="font-semibold text-[#F7E6B4]" data-testid={`total-sent-${c}`}>
                  {formatMoney(v, c)}
                </div>
              ))
            ) : (
              <div className="text-sm text-[#F7E6B4]/55">—</div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border gold-border bg-[rgba(230,180,85,0.04)]">
          <Filter size={12} className="text-[#E6B455]" />
          <span className="text-[10px] uppercase tracking-wider text-[#E6B455] font-medium">
            Filter
          </span>
        </div>
        {["all", "received", "sent"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${
              filter === f
                ? "btn-gold"
                : "btn-outline-gold"
            }`}
            data-testid={`filter-${f}`}
          >
            {f}
          </button>
        ))}
        <select
          value={currencyFilter}
          onChange={(e) => setCurrencyFilter(e.target.value)}
          className="ml-auto bg-[rgba(16,22,42,0.6)] border gold-border rounded-full px-3 py-1.5 text-xs text-[#F7E6B4] outline-none"
          data-testid="currency-filter"
        >
          <option value="all">All currencies</option>
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2" data-testid="tx-list">
        {filtered.length === 0 ? (
          <div className="card-glass rounded-2xl p-10 text-center" data-testid="empty-history">
            <HistoryIcon size={32} className="text-[#F7E6B4]/40 mx-auto mb-3" />
            <p className="text-sm text-[#F7E6B4]/60">No transactions found.</p>
          </div>
        ) : (
          filtered.map((t) => (
            <div
              key={t.id}
              className="card-glass rounded-xl p-4 flex items-center gap-3"
              data-testid={`tx-item-${t.id}`}
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  t.direction === "received"
                    ? "bg-[rgba(230,180,85,0.12)] text-[#E6B455]"
                    : "bg-[rgba(247,230,180,0.06)] text-[#F7E6B4]/70"
                }`}
              >
                {t.direction === "received" ? (
                  <ArrowDownLeft size={18} />
                ) : (
                  <ArrowUpRight size={18} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#F7E6B4] text-sm truncate">
                    {METHOD_LABEL[t.method] || t.method}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgba(230,180,85,0.1)] text-[#E6B455]">
                    {t.direction}
                  </span>
                </div>
                <div className="text-[11px] text-[#F7E6B4]/55 mt-0.5 truncate">
                  {new Date(t.createdAt).toLocaleString()}
                  {t.customer && ` · ${t.customer}`}
                  {t.merchantId && ` · ${t.merchantId}`}
                  {t.note && ` · ${t.note}`}
                </div>
              </div>
              <div
                className={`text-right font-bold ${
                  t.direction === "received" ? "text-[#E6B455]" : "text-[#F7E6B4]/85"
                }`}
              >
                <div className="text-base">
                  {t.direction === "received" ? "+" : "-"} {formatMoney(t.amount, t.currency)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#F7E6B4]/40">
                  {t.currency}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {txs.length > 0 && (
        <button
          onClick={clearAll}
          className="mt-8 mx-auto flex items-center gap-2 text-xs text-[#F7E6B4]/50 hover:text-red-400 transition"
          data-testid="clear-history-btn"
        >
          <Trash2 size={12} /> Clear all local transactions
        </button>
      )}
    </div>
  );
}
